import { PasteIcon } from "@/assets/images/svgIcons/pasteIcon"
import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import type { BoxSessionFormikEntity } from "@/modules/boxSlotSession/entities/boxSessionFormikEntity"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { ScoreResultType } from "@/modules/scores/scoreTypes"
import { SessionBlockEntityFactory } from "@/modules/sessions/entities/sessionBlockEntity"
import type {
  ProgramInfiniteSessionFormikType,
  ProgramOnOffSessionFormikType,
  ProgramSessionInputKey,
} from "@/modules/sessions/programSessionFormikType"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { useSessionCopy } from "@/modules/sessions/stores/useSessionCopyStore"
import { EllipsisMenu } from "@/modules/ui/components/ellipsisMenu"
import type { SimpleFormik } from "@/typings/formik"

type ProgramSessionFormHeaderProps = {
  isCreation: boolean
  formik:
    | SimpleFormik<ProgramInfiniteSessionFormikType, ProgramSessionInputKey>
    | SimpleFormik<ProgramOnOffSessionFormikType, ProgramSessionInputKey>
    | SimpleFormik<BoxSessionFormikEntity, ProgramSessionInputKey>
  className?: string
} & React.ComponentProps<"div">

export function ProgramSessionFormHeader({ isCreation, formik, className, ...props }: ProgramSessionFormHeaderProps) {
  const sessionCopy = useSessionCopy()

  const onSessionPaste = () => {
    try {
      if (sessionCopy) {
        if ("restDay" in sessionCopy) void formik.setFieldValue("restDay", sessionCopy.restDay)
        if ("dayIndex" in sessionCopy) void formik.setFieldValue("dayIndex", sessionCopy.dayIndex)

        void formik.setFieldValue("name", sessionCopy.name)
        void formik.setFieldValue("gymnasticTag", sessionCopy.gymnasticTag)
        void formik.setFieldValue("weightliftingTag", sessionCopy.weightliftingTag)
        void formik.setFieldValue("cardioTag", sessionCopy.cardioTag)

        const blocks = sessionCopy.blocks.map((block) => {
          const formikCopySessionDraft = SessionBlockEntityFactory.fromEntityToFormik(block)

          if (
            block.type === SessionBlockType.SESSION_EXERCISE &&
            formikCopySessionDraft.type === SessionBlockType.SESSION_EXERCISE
          ) {
            return {
              type: SessionBlockType.SESSION_EXERCISE,
              content: {
                category: block.content.category,
                otherCategory: block.content.otherCategory,
                chrono: block.content.chrono,
                chronoAmrap: formikCopySessionDraft.content.chronoAmrap,
                chronoEmom: formikCopySessionDraft.content.chronoEmom,
                chronoForTime: formikCopySessionDraft.content.chronoForTime,
                chronoTabata: formikCopySessionDraft.content.chronoTabata,
                chronoType: block.content.chrono?.type,
                description: block.content.description,
                durationInMin: block.content.durationInMin,
                scoreType: block.content.scoreType,
                id: null,
                scoreSetConfigurations: block.content.scoreSetConfigurations,
                scoringOption:
                  block.content.scoringOption === ScoreResultType.SET_N_FOR_SCOREBOARD
                    ? block.content.scoreSetConfigurations.findIndex((scoreSet) => scoreSet.useForScoring) >= 0
                      ? `SET_N_FOR_SCOREBOARD_${block.content.scoreSetConfigurations.findIndex((scoreSet) => scoreSet.useForScoring)}`
                      : null
                    : block.content.scoringOption,
                rpe: block.content.rpe,
                title: "",
              },
            }
          } else if (formikCopySessionDraft.type === SessionBlockType.MEDIAS) {
            return {
              type: SessionBlockType.MEDIAS,
              content: {
                ...formikCopySessionDraft.content,
                id: undefined,
                medias: formikCopySessionDraft.content.medias.map((media) => ({ ...media, id: undefined })), // Ensure media IDs are not copied
              },
            }
          }

          return { ...formikCopySessionDraft, content: { ...formikCopySessionDraft.content, id: undefined } }
        })

        void formik.setFieldValue("blocks", blocks)
      }

      toast.success(word("program.session.paste.success"), {
        classNames: successToastClassName(),
      })
    } catch {
      toast.error(word("program.session.copy.error"), {
        classNames: errorToastClassName(),
      })
    }
  }

  return (
    <div className={cn("flex items-center", className)} {...props}>
      <div className="flex flex-col gap-4" style={{ maxWidth: "70%" }}>
        <Text fontStyle="TL">{isCreation ? word("sessionForm.create.title") : word("sessionForm.edit.title")}</Text>

        <Text fontStyle="BS" className="text-textDefault-body">
          {isCreation ? word("sessionForm.create.subtitle") : word("sessionForm.edit.description.subtitle")}
        </Text>
      </div>

      {sessionCopy && (
        <div className="flex flex-1 justify-end gap-2">
          <EllipsisMenu propsContent={{ side: "bottom" }}>
            <DropdownMenuItem
              key="paste"
              onClick={() => {
                onSessionPaste()
              }}
            >
              <PasteIcon color="currentColor" className="text-textDefault-heading" /> {word("global.paste")}
            </DropdownMenuItem>
          </EllipsisMenu>
        </div>
      )}
    </div>
  )
}
